import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { EliminationMatch } from '@/hooks/useLeagueEliminationMatches';

type EliminationPhase = 'octavos' | 'cuartos' | 'semifinal' | 'final';

export interface TennisChallengeEncounter {
  id: string;
  weekNumber: number;
  challengerName: string;
  challengedName: string;
  challengerPosition: number;
  challengedPosition: number;
  challengerRegistrationId: string;
  challengedRegistrationId: string;
  isUserChallenger: boolean;
  isUserChallenged: boolean;
  rivalName: string;
  rivalPosition: number;
  userPosition: number;
  result: 'won' | 'lost' | 'pending';
  subcategory: string | null;
}

export function useTennisChallengeEncounters(eventId: string, totalWeeks?: number, eliminationPhase?: string, userSubcategory?: string) {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['tennis-challenge-encounters', eventId, user?.id, totalWeeks, eliminationPhase, userSubcategory],
    queryFn: async () => {
      const emptyElimination = {} as Record<EliminationPhase, EliminationMatch[]>;
      
      if (!user?.id) return { encounters: [], userEncountersByWeek: {}, challengeTypeByWeek: {}, positionsAboveByWeek: {}, eliminationMatchesByPhase: emptyElimination };

      // 1. Get user's registration IDs for this event
      const { data: userRegs } = await supabase
        .from('registrations')
        .select('id')
        .eq('event_id', eventId)
        .eq('status', 'confirmed')
        .or(`user_id.eq.${user.id},partner_user_id.eq.${user.id}`);

      const userRegIds = new Set((userRegs || []).map(r => r.id));

      // 2. Fetch elimination matches BEFORE early return so they show regardless of user registration
      let eliminationMatchesByPhase: Record<string, EliminationMatch[]> = {};
      
      if (totalWeeks && eliminationPhase) {
        const phaseOrder: EliminationPhase[] = ['octavos', 'cuartos', 'semifinal', 'final'];
        const startIdx = phaseOrder.indexOf(eliminationPhase as EliminationPhase);
        
        if (startIdx >= 0) {
          const activePhasesFromConfig = phaseOrder.slice(startIdx);
          
          let elimQuery = supabase
            .from('tennis_challenge_results')
            .select('*')
            .eq('event_id', eventId)
            .gt('week_number', totalWeeks);
          
          if (userSubcategory) {
            elimQuery = elimQuery.eq('subcategory', userSubcategory);
          }
          
          const { data: elimResults } = await elimQuery.order('week_number', { ascending: true });
          
          if (elimResults && elimResults.length > 0) {
            const elimRegIds = new Set<string>();
            elimResults.forEach(r => {
              elimRegIds.add(r.challenger_registration_id);
              elimRegIds.add(r.challenged_registration_id);
              elimRegIds.add(r.winner_registration_id);
            });
            
            const { data: elimRegs } = await supabase
              .from('registrations')
              .select('id, player1_name')
              .in('id', Array.from(elimRegIds));
            
            const elimRegNameMap = new Map<string, string>();
            (elimRegs || []).forEach(r => elimRegNameMap.set(r.id, r.player1_name || 'Jugador'));
            
            const resultsByWeek = new Map<number, typeof elimResults>();
            elimResults.forEach(r => {
              const arr = resultsByWeek.get(r.week_number) || [];
              arr.push(r);
              resultsByWeek.set(r.week_number, arr);
            });
            
            activePhasesFromConfig.forEach((phase, idx) => {
              const weekNum = totalWeeks + 1 + idx;
              const weekResults = resultsByWeek.get(weekNum) || [];
              
              eliminationMatchesByPhase[phase] = weekResults.map((r, matchIdx) => {
                const team1Name = elimRegNameMap.get(r.challenger_registration_id) || null;
                const team2Name = elimRegNameMap.get(r.challenged_registration_id) || null;
                const isUserInMatch = userRegIds.has(r.challenger_registration_id) || userRegIds.has(r.challenged_registration_id);
                const hasWinner = !!r.winner_registration_id;
                
                return {
                  id: r.id,
                  phase: phase,
                  matchNumber: matchIdx + 1,
                  team1: team1Name ? `#${r.challenger_position} ${team1Name}` : null,
                  team2: team2Name ? `#${r.challenged_position} ${team2Name}` : null,
                  score: null,
                  winner: hasWinner ? (r.winner_registration_id === r.challenger_registration_id ? 1 : 2) : null,
                  scheduledDate: null,
                  scheduledTime: null,
                  court: null,
                  isUserMatch: isUserInMatch,
                  isCompleted: hasWinner,
                };
              });
            });
            
            // Propagate winners to phases with no results yet
            activePhasesFromConfig.forEach((phase, idx) => {
              if (idx === 0) return;
              const currentMatches = eliminationMatchesByPhase[phase] || [];
              if (currentMatches.length > 0) return;

              const prevPhase = activePhasesFromConfig[idx - 1];
              const prevMatches = eliminationMatchesByPhase[prevPhase] || [];
              if (prevMatches.length === 0) return;

              const numMatches = Math.max(1, Math.floor(prevMatches.length / 2));
              eliminationMatchesByPhase[phase] = Array.from({ length: numMatches }, (_, matchIdx) => {
                const match1 = prevMatches[matchIdx * 2];
                const match2 = prevMatches[matchIdx * 2 + 1];
                const projectedTeam1 = match1?.winner
                  ? (match1.winner === 1 ? match1.team1 : match1.team2)
                  : match1?.team1 || null;
                const projectedTeam2 = match2?.winner
                  ? (match2.winner === 1 ? match2.team1 : match2.team2)
                  : match2?.team1 || null;

                return {
                  id: `projected-${phase}-${matchIdx}`,
                  phase,
                  matchNumber: matchIdx + 1,
                  team1: projectedTeam1,
                  team2: projectedTeam2,
                  score: null,
                  winner: null,
                  scheduledDate: null,
                  scheduledTime: null,
                  court: null,
                  isUserMatch: !!(match1?.isUserMatch || match2?.isUserMatch),
                  isCompleted: false,
                };
              });
            });
          } else {
            // Fallback: auto-generate projected brackets from ladder rankings
            const subcategoryFilter = userSubcategory || null;
            
            let rankingsQuery = supabase
              .from('league_ladder_rankings')
              .select('registration_id, position, player1_name, subcategory')
              .eq('event_id', eventId)
              .eq('encounter_number', totalWeeks)
              .eq('snapshot_type', 'after')
              .order('position', { ascending: true });
            
            if (subcategoryFilter) {
              rankingsQuery = rankingsQuery.eq('subcategory', subcategoryFilter);
            }
            
            const { data: latestRankings } = await rankingsQuery;
            
            if (latestRankings && latestRankings.length > 0) {
              // Group by subcategory
              const rankingsBySubcat = new Map<string, typeof latestRankings>();
              latestRankings.forEach(r => {
                const key = r.subcategory || '__default__';
                const arr = rankingsBySubcat.get(key) || [];
                arr.push(r);
                rankingsBySubcat.set(key, arr);
              });
              
              // Determine pairings per phase
              const phasePairings: Record<EliminationPhase, [number, number][]> = {
                octavos: [[1,16],[2,15],[3,14],[4,13],[5,12],[6,11],[7,10],[8,9]],
                cuartos: [[1,8],[2,7],[3,6],[4,5]],
                semifinal: [[1,4],[2,3]],
                final: [],
              };
              
              rankingsBySubcat.forEach((rankings) => {
                // Only generate for the first phase (e.g., semifinal)
                const firstPhase = activePhasesFromConfig[0];
                const pairs = phasePairings[firstPhase] || [];
                
                if (pairs.length > 0) {
                  eliminationMatchesByPhase[firstPhase] = pairs.map((pair, matchIdx) => {
                    const [pos1, pos2] = pair;
                    const player1 = rankings.find(r => r.position === pos1);
                    const player2 = rankings.find(r => r.position === pos2);
                    const isUserInMatch = (player1 && userRegIds.has(player1.registration_id)) || (player2 && userRegIds.has(player2.registration_id));
                    
                    return {
                      id: `projected-${firstPhase}-${matchIdx}`,
                      phase: firstPhase,
                      matchNumber: matchIdx + 1,
                      team1: player1 ? `#${player1.position} ${player1.player1_name || 'Jugador'}` : null,
                      team2: player2 ? `#${player2.position} ${player2.player1_name || 'Jugador'}` : null,
                      score: null,
                      winner: null,
                      scheduledDate: null,
                      scheduledTime: null,
                      court: null,
                      isUserMatch: !!isUserInMatch,
                      isCompleted: false,
                    };
                  });
                }
                
                // For subsequent phases, propagate projected winners from previous phase
                for (let i = 1; i < activePhasesFromConfig.length; i++) {
                  const phase = activePhasesFromConfig[i];
                  const prevPhase = activePhasesFromConfig[i - 1];
                  const prevMatches = eliminationMatchesByPhase[prevPhase] || [];
                  
                  const numMatches = Math.max(1, Math.floor(prevMatches.length / 2));
                  eliminationMatchesByPhase[phase] = Array.from({ length: numMatches }, (_, matchIdx) => {
                    const match1 = prevMatches[matchIdx * 2];
                    const match2 = prevMatches[matchIdx * 2 + 1];
                    // If match has actual winner use it, otherwise project higher seed (team1)
                    const projectedTeam1 = match1?.winner ? (match1.winner === 1 ? match1.team1 : match1.team2) : match1?.team1 || null;
                    const projectedTeam2 = match2?.winner ? (match2.winner === 1 ? match2.team1 : match2.team2) : match2?.team1 || null;
                    
                    const isUserInMatch = (projectedTeam1 && userRegIds.size > 0 && match1?.isUserMatch) || 
                                          (projectedTeam2 && userRegIds.size > 0 && match2?.isUserMatch) || false;
                    
                    return {
                      id: `projected-${phase}-${matchIdx}`,
                      phase: phase,
                      matchNumber: matchIdx + 1,
                      team1: projectedTeam1,
                      team2: projectedTeam2,
                      score: null,
                      winner: null,
                      scheduledDate: null,
                      scheduledTime: null,
                      court: null,
                      isUserMatch: !!isUserInMatch,
                      isCompleted: false,
                    };
                  });
                }
              });
            }
          }
        }
      }

      if (userRegIds.size === 0) return { encounters: [], userEncountersByWeek: {}, challengeTypeByWeek: {}, positionsAboveByWeek: {}, eliminationMatchesByPhase };

      // 2. Fetch challenge configs (challenge_type and positions_above per week)
      const { data: configs } = await supabase
        .from('league_challenge_configs')
        .select('week_number, challenge_type, positions_above')
        .eq('event_id', eventId);

      const challengeTypeByWeek: Record<number, string> = {};
      const positionsAboveByWeek: Record<number, number | null> = {};
      (configs || []).forEach(c => {
        challengeTypeByWeek[c.week_number] = c.challenge_type;
        positionsAboveByWeek[c.week_number] = c.positions_above;
      });

      // 3. Fetch all challenge encounters for this event
      const { data: encounters } = await supabase
        .from('league_challenge_encounters')
        .select('*')
        .eq('event_id', eventId)
        .order('week_number', { ascending: true });

      if (!encounters || encounters.length === 0) return { encounters: [], userEncountersByWeek: {}, challengeTypeByWeek, positionsAboveByWeek };

      // 3. Get all unique registration IDs to fetch names
      const allRegIds = new Set<string>();
      encounters.forEach(e => {
        allRegIds.add(e.challenger_registration_id);
        allRegIds.add(e.challenged_registration_id);
      });

      const { data: regs } = await supabase
        .from('registrations')
        .select('id, player1_name')
        .in('id', Array.from(allRegIds));

      const regNameMap = new Map<string, string>();
      (regs || []).forEach(r => regNameMap.set(r.id, r.player1_name || 'Jugador'));

      // 4. Fetch results
      const { data: results } = await supabase
        .from('tennis_challenge_results')
        .select('*')
        .eq('event_id', eventId);

      // Map results by week+challenger+challenged for quick lookup
      const resultMap = new Map<string, string>();
      (results || []).forEach(r => {
        const key = `${r.week_number}-${r.challenger_registration_id}-${r.challenged_registration_id}`;
        resultMap.set(key, r.winner_registration_id);
      });

      // 5. Build user encounters by week
      const userEncountersByWeek: Record<number, TennisChallengeEncounter> = {};
      const allMapped: TennisChallengeEncounter[] = [];

      encounters.forEach(e => {
        const isUserChallenger = userRegIds.has(e.challenger_registration_id);
        const isUserChallenged = userRegIds.has(e.challenged_registration_id);
        const challengerName = regNameMap.get(e.challenger_registration_id) || 'Jugador';
        const challengedName = regNameMap.get(e.challenged_registration_id) || 'Jugador';

        const resultKey = `${e.week_number}-${e.challenger_registration_id}-${e.challenged_registration_id}`;
        const winnerId = resultMap.get(resultKey);

        let result: 'won' | 'lost' | 'pending' = 'pending';
        if (winnerId) {
          if (isUserChallenger) {
            result = winnerId === e.challenger_registration_id ? 'won' : 'lost';
          } else if (isUserChallenged) {
            result = winnerId === e.challenged_registration_id ? 'won' : 'lost';
          }
        }

        const mapped: TennisChallengeEncounter = {
          id: e.id,
          weekNumber: e.week_number,
          challengerName,
          challengedName,
          challengerPosition: e.challenger_position,
          challengedPosition: e.challenged_position,
          challengerRegistrationId: e.challenger_registration_id,
          challengedRegistrationId: e.challenged_registration_id,
          isUserChallenger,
          isUserChallenged,
          rivalName: isUserChallenger ? challengedName : challengerName,
          rivalPosition: isUserChallenger ? e.challenged_position : e.challenger_position,
          userPosition: isUserChallenger ? e.challenger_position : e.challenged_position,
          result,
          subcategory: e.subcategory,
        };

        allMapped.push(mapped);

        if (isUserChallenger || isUserChallenged) {
          userEncountersByWeek[e.week_number] = mapped;
        }
      });

      return { encounters: allMapped, userEncountersByWeek, challengeTypeByWeek, positionsAboveByWeek, eliminationMatchesByPhase };
    },
    enabled: !!eventId && !!user?.id,
  });

  return {
    encounters: data?.encounters || [],
    userEncountersByWeek: data?.userEncountersByWeek || {},
    challengeTypeByWeek: data?.challengeTypeByWeek || {},
    positionsAboveByWeek: data?.positionsAboveByWeek || {},
    eliminationMatchesByPhase: (data?.eliminationMatchesByPhase || {}) as Record<EliminationPhase, EliminationMatch[]>,
    isLoading,
  };
}
